import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  passwordRequirementsText,
  validatePassword,
} from '@rivethealth/core/password-requirements';
import jwtDecode from 'jwt-decode';
import { catchError, of, tap } from 'rxjs';
import { AuthService } from '../../api/auth.service';
import { PasswordService } from '../../api/password.service';
import { HookDirective } from '../../common/core/hook';
import {
  useEffect,
  useState,
  useSubscriptionLatest,
  useTriggerLoad,
} from '../../common/core/hooks';

@Component({
  host: { class: 'rv-grid large outer outer-xlarge' },
  selector: 'create-login',
  styleUrls: ['./create-login.component.scss'],
  templateUrl: './create-login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateLoginComponent extends HookDirective {
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly passwordService: PasswordService,
    private readonly authService: AuthService,
    readonly _cdr: ChangeDetectorRef,
  ) {
    super(_cdr);
  }

  @Input() password: string = '';

  passwordRequirementsText = passwordRequirementsText;

  openIntercom() {
    Intercom('show');
  }

  get _() {
    const [repeatPassword, setRepeatPassword] = useState<string>('');

    const [showPassword, setShowPassword] = useState<boolean>(false);

    const [submitted, setSubmitted] = useState<boolean>(false);
    const [submittingError, setSubmittingError] = useState<string>('');

    const [resend, setResend] = useState<boolean>(false);

    const [clientSideError, setClientSideError] = useState<string>('');
    const [clientSideRepeatError, setClientSideRepeatError] =
      useState<string>('');

    const [email, setEmail] = useState<string>('');
    const [token, setToken] = useState<string>('');
    const [canSubmit, setCanSubmit] = useState<boolean>(false);
    const [expired, setExpired] = useState<boolean>(false);

    const queryParamMap = useSubscriptionLatest(this.route.queryParamMap, []);

    useEffect(() => {
      const email = queryParamMap!.get('email')!;
      const token = queryParamMap!.get('token')!;
      setEmail(email);
      setToken(token);
    }, [queryParamMap]);

    useEffect(() => {
      if (!token) {
        return;
      }
      const expiryUnixMillis = jwtDecode<{ exp: number }>(token).exp * 1000;
      // 120-second margin so it (hopefully) doesn't expire while they're
      // on this page
      if (expiryUnixMillis < Date.now() + 120_000) {
        setExpired(true);
      }
    }, [token]);

    const [submitLoad, doSubmit] = useTriggerLoad(() => {
      if (this.password !== repeatPassword) {
        setClientSideRepeatError('Passwords do not match');
        return of(null);
      }
      const complexityError = validatePassword(this.password).error;
      setClientSideError(complexityError);
      if (complexityError.length > 0) {
        return of(null);
      }
      setSubmittingError('');
      setResend(false);
      return this.passwordService
        .createPassword(email, this.password, token)
        .pipe(
          catchError((e: any) => {
            if (e instanceof HttpErrorResponse) {
              switch (e.status) {
                case 403:
                case 401:
                  setSubmittingError('Your account creation has expired.');
                  setResend(true);
                  break;
                default:
                  setSubmittingError(
                    "We're having issues. Please cancel and try again or contact support.",
                  );
              }
            }
            throw e;
          }),
          tap(() => {
            setSubmitted(true);
          }),
        );
    });

    const [resendLoad, doResend] = useTriggerLoad(() => {
      return this.passwordService.sendResetEmail(email).pipe(
        catchError((e: any) => {
          setSubmittingError(
            "We're having issues. Please cancel and try again or contact support.",
          );
          throw e;
        }),
        tap(() => {
          this.router.navigate(['login', 'sent'], {
            queryParams: {
              email: email,
            },
            replaceUrl: true,
          });
        }),
      );
    });

    const checkForm = () => {
      setCanSubmit(!!email && !!this.password && !!repeatPassword);
    };

    return {
      email,
      repeatPassword,
      setRepeatPassword,
      showPassword,
      setShowPassword,
      submitted,
      submitLoad,
      token,
      submittingError,
      resend,
      resendLoad,
      clientSideError,
      setClientSideError,
      clientSideRepeatError,
      setClientSideRepeatError,
      doSubmit,
      doResend,
      canSubmit,
      checkForm,
      expired,
    };
  }
}
